import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import authService from '../services/authService';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import '../styles/my-bookings.css';

const MyBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [campingBookings, setCampingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('packages');
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('latest');
    const [payingBookingId, setPayingBookingId] = useState(null);

    const getToken = () => localStorage.getItem('token');

    const fetchUserBookings = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const token = getToken();
            if (!token) {
                setError('Please login to view your bookings');
                setLoading(false);
                return;
            }

            try {
                const packageResponse = await axios.get(buildApiUrl('/bookings/my-bookings'), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (packageResponse.data.success) {
                    setBookings(packageResponse.data.data || []);
                }
            } catch (err) {
                console.error('Error fetching package bookings:', err);
                setBookings([]);
            }

            try {
                const campingResponse = await axios.get(buildApiUrl('/bookings/my-camping-bookings'), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (campingResponse.data.success) {
                    setCampingBookings(campingResponse.data.data || []);
                }
            } catch (err) {
                console.error('Error fetching camping bookings:', err);
                setCampingBookings([]);
            }
        } catch (fetchError) {
            console.error('Error fetching bookings:', fetchError);
            setError('Failed to load bookings. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = authService.getToken();
        const userData = authService.getCurrentUser();

        setIsLoggedIn(!!token);
        setUser(userData);

        if (token && userData) {
            fetchUserBookings();
        } else {
            setLoading(false);
        }
    }, [fetchUserBookings]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        if (!price) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(parseFloat(price));
    };

    const getDueAmount = (booking) => {
        return Math.max(Number(booking.total_amount || 0) - Number(booking.advance_amount || 0), 0);
    };

    const handlePayBalance = async (booking) => {
        try {
            setPayingBookingId(booking.booking_id);
            const response = await axios.post(buildApiUrl('/payments/balance/create-order'), {
                booking_id: booking.booking_id,
                customer_email: user?.email || booking.customer_email || ''
            });

            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Unable to start balance payment');
            }

            const order = response.data;
            const razorpay = new window.Razorpay({
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: order.amount,
                currency: order.currency,
                name: 'RedGoTrip',
                description: `Balance Payment for ${booking.package_title || booking.site_name}`,
                order_id: order.orderId,
                prefill: {
                    name: user?.name || user?.username || booking.customer_name,
                    email: user?.email || booking.customer_email,
                    contact: booking.customer_phone
                },
                handler: async (paymentResponse) => {
                    await axios.post(buildApiUrl('/payments/balance/verify'), {
                        razorpay_order_id: paymentResponse.razorpay_order_id,
                        razorpay_payment_id: paymentResponse.razorpay_payment_id,
                        razorpay_signature: paymentResponse.razorpay_signature,
                        booking_id: booking.booking_id
                    });

                    await fetchUserBookings();
                    window.alert('Balance payment completed successfully.');
                },
                modal: {
                    ondismiss: () => setPayingBookingId(null)
                },
                theme: {
                    color: '#1f5ca8'
                }
            });

            razorpay.open();
        } catch (error) {
            console.error('Balance payment error:', error);
            window.alert(error.response?.data?.message || error.message || 'Failed to process balance payment');
        } finally {
            setPayingBookingId(null);
        }
    };

    const cancelBooking = async (booking, type) => {
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;

        try {
            const token = getToken();
            const isPackageBooking = type === 'packages';
            const bookingIdentifier = isPackageBooking ? booking.id : booking.booking_id;
            const endpoint = isPackageBooking
                ? buildApiUrl(`/bookings/${bookingIdentifier}/cancel`)
                : buildApiUrl(`/camping/booking/${bookingIdentifier}/cancel`);

            await axios.post(endpoint, { email: user?.email || booking.customer_email || '' }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Booking cancelled successfully!');
            fetchUserBookings();
        } catch (cancelError) {
            console.error('Error cancelling booking:', cancelError);
            alert(cancelError.response?.data?.message || 'Failed to cancel booking');
        }
    };

    const activeBookings = activeTab === 'packages' ? bookings : campingBookings;

    const stats = useMemo(() => {
        const allBookings = [...bookings, ...campingBookings];
        return {
            total: allBookings.length,
            packageCount: bookings.length,
            campingCount: campingBookings.length,
            confirmed: allBookings.filter((item) => item.booking_status === 'confirmed').length
        };
    }, [bookings, campingBookings]);

    const filteredBookings = useMemo(() => {
        let results = [...activeBookings];

        const query = searchTerm.trim().toLowerCase();
        if (query) {
            results = results.filter((booking) => {
                const title = activeTab === 'packages' ? booking.package_title : booking.site_name;
                const location = activeTab === 'packages' ? booking.destinations : booking.location_name;
                return [
                    booking.booking_id,
                    booking.transaction_id,
                    title,
                    location
                ]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(query));
            });
        }

        if (statusFilter !== 'all') {
            if (statusFilter === 'payment_pending') {
                results = results.filter((booking) => booking.payment_status === 'pending');
            } else if (statusFilter === 'payment_completed') {
                results = results.filter((booking) => booking.payment_status === 'completed');
            } else {
                results = results.filter((booking) => booking.booking_status === statusFilter);
            }
        }

        results.sort((a, b) => {
            if (sortBy === 'amount_high') return Number(b.total_amount || 0) - Number(a.total_amount || 0);
            if (sortBy === 'amount_low') return Number(a.total_amount || 0) - Number(b.total_amount || 0);

            const dateA = new Date(a.created_at || 0).getTime();
            const dateB = new Date(b.created_at || 0).getTime();
            return sortBy === 'oldest' ? dateA - dateB : dateB - dateA;
        });

        return results;
    }, [activeBookings, activeTab, searchTerm, statusFilter, sortBy]);

    const hasBookings = filteredBookings.length > 0;

    if (!isLoggedIn) {
        return (
            <div className="my-bookings-page">
                <div className="my-bookings-header">
                    <div className="header-content">
                        <span className="bookings-badge">Travel Dashboard</span>
                        <h1>My Bookings</h1>
                        <p>Manage and track all your travel bookings in one place.</p>
                    </div>
                </div>
                <div className="my-bookings-container">
                    <div className="auth-card">
                        <div className="auth-icon">
                            <i className="fas fa-lock"></i>
                        </div>
                        <h2>Login Required</h2>
                        <p>Please login to view and manage your bookings.</p>
                        <Link to="/login" className="auth-btn-primary">Login Now</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="my-bookings-page">
                <div className="my-bookings-header">
                    <div className="header-content">
                        <span className="bookings-badge">Travel Dashboard</span>
                        <h1>My Bookings</h1>
                        <p>Manage and track all your travel bookings in one place.</p>
                    </div>
                </div>
                <div className="my-bookings-container">
                    <div className="loading-card">
                        <div className="loading-spinner"></div>
                        <p>Loading your bookings...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-bookings-page">
            <div className="my-bookings-header">
                <div className="header-content">
                    <span className="bookings-badge">Travel Dashboard</span>
                    <h1>My Bookings</h1>
                    <p>Track your trips, payment status, and upcoming plans from one clean dashboard.</p>
                    <div className="header-highlights">
                        <div className="header-highlight">
                            <i className="fas fa-box"></i>
                            <span>{stats.packageCount} Packages</span>
                        </div>
                        <div className="header-highlight">
                            <i className="fas fa-campground"></i>
                            <span>{stats.campingCount} Camps</span>
                        </div>
                        <div className="header-highlight">
                            <i className="fas fa-circle-check"></i>
                            <span>{stats.confirmed} Confirmed</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="my-bookings-container">
                {user && (
                    <div className="welcome-section">
                        <div className="welcome-avatar">
                            {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="welcome-copy">
                            <span className="welcome-eyebrow">Traveler Profile</span>
                            <h2>Welcome back, {(user.name || user.username || 'Traveler').split(' ')[0]}!</h2>
                            <p>Here is a quick view of your packages, camping stays and payment progress.</p>
                        </div>
                    </div>
                )}

                <div className="stats-cards">
                    <div className="stat-card">
                        <div className="stat-icon package">
                            <i className="fas fa-box"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.packageCount}</h3>
                            <p>Package Bookings</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon camping">
                            <i className="fas fa-campground"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.campingCount}</h3>
                            <p>Camping Bookings</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <i className="fas fa-suitcase-rolling"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.total}</h3>
                            <p>Total Trips</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon confirmed">
                            <i className="fas fa-circle-check"></i>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.confirmed}</h3>
                            <p>Confirmed Bookings</p>
                        </div>
                    </div>
                </div>

                <div className="booking-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'packages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('packages')}
                    >
                        <i className="fas fa-box"></i>
                        <span>Package Bookings</span>
                        {bookings.length > 0 && <span className="tab-badge">{bookings.length}</span>}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'camping' ? 'active' : ''}`}
                        onClick={() => setActiveTab('camping')}
                    >
                        <i className="fas fa-campground"></i>
                        <span>Camping Bookings</span>
                        {campingBookings.length > 0 && <span className="tab-badge">{campingBookings.length}</span>}
                    </button>
                </div>

                <div className="bookings-toolbar">
                    <div className="toolbar-copy">
                        <span className="toolbar-label">{activeTab === 'packages' ? 'Package Trips' : 'Camping Stays'}</span>
                        <h3>{filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''} available</h3>
                        <p>Review booking status, payment progress and next travel dates from one place.</p>
                    </div>
                    <button type="button" className="toolbar-refresh" onClick={fetchUserBookings}>
                        <i className="fas fa-rotate-right"></i>
                        Refresh
                    </button>
                </div>

                <div className="bookings-controls">
                    <div className="control-search">
                        <i className="fas fa-magnifying-glass"></i>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={`Search by ${activeTab === 'packages' ? 'package' : 'camping site'} or booking ID`}
                        />
                    </div>

                    <div className="control-selects">
                        <div className="control-select">
                            <label>Status</label>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All Status</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="payment_pending">Payment Pending</option>
                                <option value="payment_completed">Payment Completed</option>
                            </select>
                        </div>

                        <div className="control-select">
                            <label>Sort By</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                <option value="latest">Latest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="amount_high">Amount: High to Low</option>
                                <option value="amount_low">Amount: Low to High</option>
                            </select>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="error-alert">
                        <div className="error-copy">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>{error}</span>
                        </div>
                        <button onClick={fetchUserBookings} className="retry-button">Try Again</button>
                    </div>
                )}

                {!hasBookings ? (
                    <div className="empty-state">
                        <div className="empty-icon">
                            <i className="fas fa-calendar-check"></i>
                        </div>
                        <h3>No Bookings Found</h3>
                        <p>
                            {activeBookings.length === 0
                                ? `You haven't made any ${activeTab === 'packages' ? 'package' : 'camping'} bookings yet.`
                                : 'No bookings match your current search or filters.'}
                        </p>
                        <Link to={activeTab === 'packages' ? '/packages' : '/camping'} className="explore-button">
                            <i className="fas fa-arrow-right"></i>
                            Explore {activeTab === 'packages' ? 'Packages' : 'Campsites'}
                        </Link>
                    </div>
                ) : (
                    <div className="bookings-grid">
                        {filteredBookings.map((booking) => {
                            const isPackageBooking = activeTab === 'packages';
                            const title = isPackageBooking ? booking.package_title : booking.site_name;
                            return (
                                <div key={booking.id} className="booking-card">
                                    

                                    <div className="card-main">
                                        <div className="card-body">
                                            <div className="card-topline">
                                                <div className="booking-id-section">
                                                    <span className="id-label">Booking ID</span>
                                                    <span className="id-value">{booking.booking_id}</span>
                                                </div>
                                                <div className="booking-date">
                                                    <i className="far fa-calendar-alt"></i>
                                                    {formatDateTime(booking.created_at)}
                                                </div>
                                            </div>

                                            <div className="package-info">
                                                <div className="package-details">
                                                    <h3 className="booking-title">{title}</h3>
                                                    <div className="booking-meta">
                                                        {isPackageBooking ? (
                                                            <>
                                                                <span className="meta-item">
                                                                    <i className="fas fa-clock"></i>
                                                                    {booking.duration}
                                                                </span>
                                                                <span className="meta-item">
                                                                    <i className="fas fa-map-marker-alt"></i>
                                                                    {booking.destinations}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="meta-item">
                                                                    <i className="fas fa-map-marker-alt"></i>
                                                                    {booking.location_name}
                                                                </span>
                                                                <span className="meta-item">
                                                                    <i className="fas fa-bed"></i>
                                                                    {booking.bed_type || 'Sleeping Bag'}
                                                                </span>
                                                                <span className="meta-item">
                                                                    <i className="fas fa-tag"></i>
                                                                    {booking.site_type}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="booking-details-grid">
                                                {isPackageBooking ? (
                                                    <>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Travel Date</span>
                                                            <span className="detail-value">{formatDate(booking.travel_date)}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Travelers</span>
                                                            <span className="detail-value">
                                                                {booking.adults} Adult{booking.adults > 1 ? 's' : ''}
                                                                {booking.children > 0 ? `, ${booking.children} Child${booking.children > 1 ? 'ren' : ''}` : ''}
                                                            </span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Price per Person</span>
                                                            <span className="detail-value">{formatPrice(booking.price)}</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Check-in</span>
                                                            <span className="detail-value">{formatDate(booking.check_in)}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Check-out</span>
                                                            <span className="detail-value">{formatDate(booking.check_out)}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Nights</span>
                                                            <span className="detail-value">{booking.total_nights}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Guests</span>
                                                            <span className="detail-value">{booking.guests}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Price per Night</span>
                                                            <span className="detail-value">{formatPrice(booking.price_per_night)}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {booking.special_requests && (
                                                <div className="special-requests">
                                                    <i className="fas fa-comment-dots"></i>
                                                    <span>{booking.special_requests}</span>
                                                </div>
                                            )}
                                        </div>

                                        <aside className="card-side">
                                            <div className="card-price">
                                                <div className="price-details">
                                                    <span className="price-label">Total Amount</span>
                                                    <span className="price-value">{formatPrice(booking.total_amount)}</span>
                                                </div>
                                                {booking.advance_amount > 0 && booking.advance_amount !== booking.total_amount && (
                                                    <div className="advance-details">
                                                        <span className="advance-label">Advance Paid</span>
                                                        <span className="advance-value">{formatPrice(booking.advance_amount)}</span>
                                                    </div>
                                                )}
                                                {getDueAmount(booking) > 0 && (
                                                    <div className="advance-details due-details">
                                                        <span className="advance-label">Balance Due</span>
                                                        <span className="advance-value">{formatPrice(getDueAmount(booking))}</span>
                                                    </div>
                                                )}
                                                {booking.transaction_id && (
                                                    <div className="transaction-details">
                                                        <span className="transaction-label">Transaction ID</span>
                                                        <span className="transaction-value">{booking.transaction_id}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="card-actions">
                                                <Link
                                                    to={isPackageBooking ? `/booking/track/${booking.booking_id}` : `/camping-booking-success/${booking.booking_id}`}
                                                    className="action-btn view-btn"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                    View Details
                                                </Link>
                                                {booking.booking_status !== 'cancelled' && booking.booking_status !== 'completed' && (
                                                    <button
                                                        className="action-btn cancel-btn"
                                                        onClick={() => cancelBooking(booking, activeTab)}
                                                    >
                                                        <i className="fas fa-times-circle"></i>
                                                        Cancel
                                                    </button>
                                                )}
                                                {isPackageBooking && getDueAmount(booking) > 0 && booking.booking_status !== 'cancelled' && (
                                                    <button
                                                        className="action-btn due-btn"
                                                        onClick={() => handlePayBalance(booking)}
                                                        disabled={payingBookingId === booking.booking_id}
                                                    >
                                                        <i className={`fas ${payingBookingId === booking.booking_id ? 'fa-spinner fa-spin' : 'fa-credit-card'}`}></i>
                                                        {payingBookingId === booking.booking_id ? 'Processing...' : 'Pay Balance'}
                                                    </button>
                                                )}
                                                <Link
                                                    to={isPackageBooking ? `/invoice/${booking.booking_id}` : `/invoice/${booking.booking_id}?type=camping`}
                                                    state={{ booking, type: isPackageBooking ? 'package' : 'camping' }}
                                                    className="action-btn invoice-btn"
                                                >
                                                    <i className="fas fa-download"></i>
                                                    Invoice
                                                </Link>
                                            </div>
                                        </aside>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyBookings;
